// Offline storage for volleyball match data.
//
// Durability: every mutation updates an in-memory cache immediately, then
// writes a synchronous localStorage backup of unsynced matches (survives a
// killed tab) and persists the full set to IndexedDB in the background.
//
// Robustness: each match carries a client-generated `clientId` so the server
// can dedupe retries, plus sync bookkeeping (status / attempts / lastError) so
// a permanently-failing match can be surfaced instead of retried forever.

const DB_NAME = 'volleyball_offline';
const DB_VERSION = 1;
const STORE = 'matches';

const LEGACY_STORAGE_KEY = 'volleyball_offline_matches';
const NETWORK_STATUS_KEY = 'volleyball_network_status';

export const MAX_SYNC_ATTEMPTS = 5;

export const SYNC_STATUS = {
  PENDING: 'pending',
  SYNCED: 'synced',
  FAILED: 'failed'
};

class OfflineStorage {
  constructor() {
    this.cache = [];
    this.db = null;
    this.networkChangeCallbacks = [];
    this.changeCallbacks = [];
    this._writeQueue = Promise.resolve();

    // ready resolves once IndexedDB is open and the cache is hydrated.
    this.ready = this.init();

    this.setupNetworkListeners();
    this.updateNetworkStatus();
    this.requestPersistentStorage();
  }

  // Prefer not to be evicted when the device is low on space (best-effort).
  requestPersistentStorage() {
    try {
      if (navigator.storage && typeof navigator.storage.persist === 'function') {
        navigator.storage.persist().catch(() => {});
      }
    } catch (error) {
      // Non-fatal.
    }
  }

  // ---- IndexedDB plumbing -------------------------------------------------

  openDatabase() {
    return new Promise((resolve, reject) => {
      if (typeof indexedDB === 'undefined') {
        reject(new Error('IndexedDB is not available'));
        return;
      }
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'offlineId' });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async init() {
    try {
      this.db = await this.openDatabase();
      this.cache = (await this.readAllFromDb()).map((m) => this.normalizeRecord(m));
      await this.mergeLocalBackup();
    } catch (error) {
      console.error('Failed to initialize offline storage (IndexedDB):', error);
      // Fall back to the localStorage backup so data still survives in browsers
      // where IndexedDB is blocked (e.g. private mode).
      this.cache = this.readBackupMatches().map((m) => this.normalizeRecord(m));
    }
    this.persistBackup();
    return this.cache;
  }

  readAllFromDb() {
    return new Promise((resolve, reject) => {
      if (!this.db) {
        resolve([]);
        return;
      }
      try {
        const tx = this.db.transaction(STORE, 'readonly');
        const store = tx.objectStore(STORE);
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      } catch (error) {
        reject(error);
      }
    });
  }

  enqueueWrite(task) {
    const run = this._writeQueue.then(task, task);
    this._writeQueue = run.catch((error) => {
      console.error('Offline storage write failed:', error);
    });
    return run;
  }

  persistRecord(record) {
    return this.enqueueWrite(() => new Promise((resolve) => {
      if (!this.db) {
        resolve(false);
        return;
      }
      try {
        const tx = this.db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(record);
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => {
          console.error('Error persisting offline match:', tx.error);
          resolve(false);
        };
        tx.onabort = () => {
          console.error('Offline match write aborted (quota?):', tx.error);
          resolve(false);
        };
      } catch (error) {
        console.error('Error persisting offline match:', error);
        resolve(false);
      }
    }));
  }

  persistDelete(offlineId) {
    return this.enqueueWrite(() => new Promise((resolve) => {
      if (!this.db) {
        resolve(false);
        return;
      }
      try {
        const tx = this.db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).delete(offlineId);
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
      } catch (error) {
        console.error('Error deleting offline match:', error);
        resolve(false);
      }
    }));
  }

  // Synchronous backup of anything that has not reached the server. localStorage
  // writes complete before this function returns, so a killed tab cannot drop
  // a match that was just saved.
  persistBackup() {
    try {
      const unsynced = this.cache.filter((m) => !m.synced);
      localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(unsynced));
    } catch (error) {
      console.error('Error writing offline match backup:', error);
    }
  }

  readBackupMatches() {
    try {
      const stored = localStorage.getItem(LEGACY_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error reading offline match backup:', error);
      return [];
    }
  }

  async mergeLocalBackup() {
    const backup = this.readBackupMatches();
    if (backup.length === 0) return;

    const existingIds = new Set(this.cache.map((m) => m.offlineId));
    const existingClientIds = new Set(this.cache.map((m) => m.clientId).filter(Boolean));
    let merged = 0;

    for (const match of backup) {
      if (!match.offlineId) {
        match.offlineId = this.generateOfflineId();
      }
      if (existingIds.has(match.offlineId)) continue;
      if (match.clientId && existingClientIds.has(match.clientId)) continue;
      const normalized = this.normalizeRecord(match);
      this.cache.push(normalized);
      existingIds.add(normalized.offlineId);
      if (normalized.clientId) existingClientIds.add(normalized.clientId);
      await this.persistRecord(normalized);
      merged++;
    }

    if (merged > 0) {
      console.log(`Merged ${merged} unsynced match(es) from local backup into IndexedDB`);
    }
  }

  // Ensure older records gain the new bookkeeping fields.
  normalizeRecord(match) {
    return {
      syncAttempts: 0,
      lastError: null,
      status: match.synced ? SYNC_STATUS.SYNCED : SYNC_STATUS.PENDING,
      clientId: match.clientId || this.generateClientId(),
      ...match
    };
  }

  // ---- Network status -----------------------------------------------------

  setupNetworkListeners() {
    window.addEventListener('online', () => {
      this.updateNetworkStatus();
      this.notifyNetworkChange('online');
    });
    window.addEventListener('offline', () => {
      this.updateNetworkStatus();
      this.notifyNetworkChange('offline');
    });
  }

  updateNetworkStatus() {
    try {
      localStorage.setItem(NETWORK_STATUS_KEY, JSON.stringify({
        isOnline: navigator.onLine,
        lastChecked: new Date().toISOString()
      }));
    } catch (error) {
      // Non-fatal; navigator.onLine remains the source of truth.
    }
  }

  isOnline() {
    return navigator.onLine;
  }

  getNetworkStatus() {
    try {
      const stored = localStorage.getItem(NETWORK_STATUS_KEY);
      return stored ? JSON.parse(stored) : { isOnline: navigator.onLine, lastChecked: new Date().toISOString() };
    } catch (error) {
      return { isOnline: navigator.onLine, lastChecked: new Date().toISOString() };
    }
  }

  onNetworkChange(callback) {
    this.networkChangeCallbacks.push(callback);
    return () => {
      this.networkChangeCallbacks = this.networkChangeCallbacks.filter((cb) => cb !== callback);
    };
  }

  notifyNetworkChange(status) {
    this.networkChangeCallbacks.forEach((callback) => {
      try {
        callback(status);
      } catch (error) {
        console.error('Error in network change callback:', error);
      }
    });
  }

  // ---- Change notification (lets React re-render on cache updates) ---------

  onChange(callback) {
    this.changeCallbacks.push(callback);
    return () => {
      this.changeCallbacks = this.changeCallbacks.filter((cb) => cb !== callback);
    };
  }

  notifyChange() {
    this.persistBackup();
    this.changeCallbacks.forEach((callback) => {
      try {
        callback(this.cache);
      } catch (error) {
        console.error('Error in storage change callback:', error);
      }
    });
  }

  // ---- Match CRUD (synchronous against cache, async to disk) --------------

  async saveMatchOffline(matchData) {
    const record = this.normalizeRecord({
      ...matchData,
      offlineId: this.generateOfflineId(),
      clientId: matchData.clientId || this.generateClientId(),
      savedAt: new Date().toISOString(),
      synced: false,
      status: SYNC_STATUS.PENDING,
      syncAttempts: 0,
      lastError: null
    });

    this.cache.push(record);
    this.notifyChange();
    await this.persistRecord(record);
    return record;
  }

  getOfflineMatches() {
    return [...this.cache];
  }

  findByOfflineId(offlineId) {
    return this.cache.find((m) => m.offlineId === offlineId) || null;
  }

  findByClientId(clientId) {
    if (!clientId) return null;
    return this.cache.find((m) => m.clientId === clientId) || null;
  }

  // Matches eligible for an automatic sync attempt: not yet synced and not
  // parked in the failed state (those wait for an explicit retry).
  getUnsyncedMatches() {
    return this.cache.filter((m) => !m.synced && m.status !== SYNC_STATUS.FAILED);
  }

  getPendingCount() {
    return this.cache.filter((m) => !m.synced).length;
  }

  getFailedMatches() {
    return this.cache.filter((m) => m.status === SYNC_STATUS.FAILED);
  }

  async markMatchAsSynced(offlineId, serverResponse = null) {
    const match = this.findByOfflineId(offlineId);
    if (!match) return false;

    match.synced = true;
    match.status = SYNC_STATUS.SYNCED;
    match.syncedAt = new Date().toISOString();
    match.lastError = null;
    if (serverResponse) {
      match.serverId = serverResponse._id;
      if (serverResponse.clientId) match.clientId = serverResponse.clientId;
    }

    this.notifyChange();
    await this.persistRecord(match);
    return true;
  }

  // Drop a local copy once we know the server has it, so the offline list
  // only shows work that still needs attention.
  async dropSyncedMatch(offlineId) {
    const match = this.findByOfflineId(offlineId);
    if (!match) return false;
    this.cache = this.cache.filter((m) => m.offlineId !== offlineId);
    this.notifyChange();
    await this.persistDelete(offlineId);
    return true;
  }

  // Record an unsuccessful sync attempt. Network blips do not count toward the
  // cap — only server/validation failures do, so flaky gym Wi‑Fi cannot park
  // a match as permanently failed.
  async recordSyncFailure(offlineId, errorMessage, { permanent = false, countTowardCap = true } = {}) {
    const match = this.findByOfflineId(offlineId);
    if (!match) return false;

    match.lastError = errorMessage || 'Unknown error';
    match.lastAttemptAt = new Date().toISOString();
    if (countTowardCap) {
      match.syncAttempts = (match.syncAttempts || 0) + 1;
    }
    if (permanent || (countTowardCap && match.syncAttempts >= MAX_SYNC_ATTEMPTS)) {
      match.status = SYNC_STATUS.FAILED;
    }

    this.notifyChange();
    await this.persistRecord(match);
    return match.status === SYNC_STATUS.FAILED;
  }

  async resetFailedMatches() {
    let count = 0;
    for (const match of this.cache) {
      if (match.status === SYNC_STATUS.FAILED) {
        match.status = SYNC_STATUS.PENDING;
        match.syncAttempts = 0;
        match.lastError = null;
        await this.persistRecord(match);
        count++;
      }
    }
    if (count > 0) this.notifyChange();
    return count;
  }

  async cleanupSyncedMatches(keepDays = 7) {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - keepDays);

    const toRemove = this.cache.filter((match) => {
      if (!match.synced) return false;
      const syncedDate = new Date(match.syncedAt || match.savedAt);
      return syncedDate <= cutoff;
    });

    if (toRemove.length === 0) return 0;

    const removeIds = new Set(toRemove.map((m) => m.offlineId));
    this.cache = this.cache.filter((m) => !removeIds.has(m.offlineId));
    this.notifyChange();
    for (const match of toRemove) {
      await this.persistDelete(match.offlineId);
    }
    console.log(`Cleaned up ${toRemove.length} old synced match(es)`);
    return toRemove.length;
  }

  async updateOfflineMatch(offlineId, updates) {
    const match = this.findByOfflineId(offlineId);
    if (!match) return null;

    Object.assign(match, updates, {
      updatedAt: new Date().toISOString(),
      synced: false,
      status: SYNC_STATUS.PENDING,
      syncAttempts: 0,
      lastError: null
    });

    this.notifyChange();
    await this.persistRecord(match);
    return match;
  }

  async deleteOfflineMatch(offlineId) {
    const before = this.cache.length;
    this.cache = this.cache.filter((m) => m.offlineId !== offlineId);
    if (this.cache.length === before) return false;
    this.notifyChange();
    await this.persistDelete(offlineId);
    return true;
  }

  generateOfflineId() {
    return `offline_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }

  generateClientId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  getStorageInfo() {
    const totalMatches = this.cache.length;
    const pending = this.getPendingCount();
    const failed = this.getFailedMatches().length;
    let sizeInBytes = 0;
    try {
      sizeInBytes = new Blob([JSON.stringify(this.cache)]).size;
    } catch (error) {
      sizeInBytes = 0;
    }
    return {
      totalMatches,
      unsyncedMatches: pending,
      failedMatches: failed,
      storageSize: `${(sizeInBytes / 1024).toFixed(2)} KB`,
      sizeInBytes
    };
  }

  async clearAllOfflineData() {
    this.cache = [];
    this.notifyChange();
    if (!this.db) return true;
    return this.enqueueWrite(() => new Promise((resolve) => {
      try {
        const tx = this.db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).clear();
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
      } catch (error) {
        console.error('Error clearing offline data:', error);
        resolve(false);
      }
    }));
  }

  exportOfflineData() {
    return {
      matches: this.getOfflineMatches(),
      networkStatus: this.getNetworkStatus(),
      storageInfo: this.getStorageInfo(),
      exportedAt: new Date().toISOString()
    };
  }
}

const offlineStorage = new OfflineStorage();

export default offlineStorage;
